/**
 * «Captación por origen» en la ficha de una cirugía del catálogo (V2, 28/9/2026).
 *
 * El KPI de dirección «Captación por origen» puede filtrar por procedimiento.
 * Cuál, cómo se llama en el filtro y con qué palabras se reconoce una consulta
 * de ese procedimiento (en la agenda, la nota o el concepto de la cita) es un
 * DATO de la cirugía, no del código. Solo lo cambia un administrador, por su
 * propia puerta (`PUT /api/surgeries/{id}/captacion`), y queda en el rastro.
 *
 * Cómo se escribe una palabra (lo explica también el propio formulario):
 *   palabra · pala* (empieza por) · una frase · uno * dos (como mucho una en medio)
 */
import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';

// Lo que el PUT de la cirugía no manda (tiene su propia puerta).
export const CAMPOS_CAPTACION = [
  'captacion', 'etiqueta_captacion', 'palabras_captacion', 'excluye_captacion',
  'captacion_actualizado_at', 'captacion_actualizado_por',
];

const lineas = (lista) => (Array.isArray(lista) ? lista : []).join('\n');
const deLineas = (texto) => String(texto || '').split('\n').map((l) => l.trim()).filter(Boolean);

/** El estado del formulario a partir de la cirugía leída. */
export function captacionInicial(cirugia) {
  return {
    captacion: cirugia?.captacion === true,
    etiqueta: cirugia?.etiqueta_captacion || '',
    palabras: lineas(cirugia?.palabras_captacion),
    excluye: lineas(cirugia?.excluye_captacion),
  };
}

/** El cuerpo del PUT de captación. */
export function cuerpoCaptacion(estado) {
  return {
    captacion: !!estado.captacion,
    etiqueta_captacion: String(estado.etiqueta || '').trim(),
    palabras_captacion: deLineas(estado.palabras),
    excluye_captacion: deLineas(estado.excluye),
  };
}

/** ¿Ha cambiado algo respecto a lo leído? */
export function captacionCambiada(estado, cirugia) {
  return JSON.stringify(cuerpoCaptacion(estado))
    !== JSON.stringify(cuerpoCaptacion(captacionInicial(cirugia)));
}

export default function CaptacionCampos({ estado, onChange, nombre }) {
  const set = (k, v) => onChange({ ...estado, [k]: v });
  return (
    <div className="md:col-span-2 border-t border-border pt-3 mt-1" data-testid="surgery-captacion-bloque">
      <label className="flex items-center gap-2 text-sm font-semibold text-[#13231E]">
        <input
          type="checkbox"
          checked={estado.captacion}
          onChange={(e) => set('captacion', e.target.checked)}
          data-testid="surgery-captacion"
        />
        Filtro de «Captación por origen»
      </label>
      <p className="text-xs text-muted-foreground mt-1 mb-2">
        Si se marca, el KPI de dirección «Captación por origen» ofrece contar solo las primeras
        valoraciones de este procedimiento: las que lo dicen en el nombre o el código de su agenda,
        en su nota o en su concepto. Solo administradores; el cambio queda en el rastro.
      </p>
      {estado.captacion && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <Label className="text-xs">Nombre en el filtro (opcional)</Label>
            <Input
              value={estado.etiqueta}
              onChange={(e) => set('etiqueta', e.target.value)}
              placeholder={nombre || ''}
              data-testid="surgery-captacion-etiqueta"
            />
          </div>
          <div>
            <Label className="text-xs">Palabras que lo reconocen (una por línea)</Label>
            <Textarea
              rows={4}
              value={estado.palabras}
              onChange={(e) => set('palabras', e.target.value)}
              data-testid="surgery-captacion-palabras"
            />
          </div>
          <div>
            <Label className="text-xs">Palabras que lo excluyen (una por línea)</Label>
            <Textarea
              rows={4}
              value={estado.excluye}
              onChange={(e) => set('excluye', e.target.value)}
              data-testid="surgery-captacion-excluye"
            />
          </div>
          <p className="md:col-span-2 text-[11px] text-muted-foreground">
            Sin acentos ni mayúsculas, por palabras enteras: <code>palabra</code>;{' '}
            <code>pala*</code> = empieza por «pala» (para reconocer, sigue con letras; para
            excluir, con lo que sea); <code>una frase</code>; <code>uno * dos</code> = con como
            mucho una palabra en medio. Lo que excluye manda sobre lo demás.
          </p>
        </div>
      )}
    </div>
  );
}
